/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
import {
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TableBody,
  TableFooter,
} from "@material-ui/core"

import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { transformProperty } from "utils/objects"
import useShowSnackbar from "utils/snackbar-hooks"
import DeliveryNotePicker from "./DeliveryNotePicker"
import InvoiceFormContext from "./InvoiceFormContex"
import ProductDescriptionRow from "./ProductDescriptionRow"
import { simplifyDNProductDetails } from "./utils"

const ProductDescriptionTable = forwardRef(
  ({ fetchDeliveryNoteData, initialValue, fetchInitDeliveryNoteData }, ref) => {
    const [initialFormData] = useState(() => {
      return (initialValue?.productDescriptions ?? []).map((item, index) => {
        return {
          __id: index,
          description: item.description,
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          unit: item.unit,
          dnNumber: item?.productDetail?.deliveryNote?.dnNumber,
          poNumber:
            item?.productDetail?.salesEstimate?.slsPurchaseOrder?.poNumber,
          salesEstimateId: item?.productDetail?.salesEstimateId,
          idDnDetail: item.dnDetailId,
          totalCost: item.unitPrice,
        }
      })
    })
    const { register, control, setValue, errors, getValues } = useForm()
    const [showSnackbar] = useShowSnackbar()

    const { fields } = useFieldArray({
      control,
      name: "productDescriptions",
      keyName: "__id",
    })

    const {
      setProductDescriptions,
      rateIn,
      setInvoiceDate,
      invoiceDate,
    } = useContext(InvoiceFormContext)

    const [selectedDeliveryNotes, setSelectedDeliveryNotes] = useState(() => {
      return Array.from(
        new Set(
          (initialValue?.productDescriptions ?? []).map(
            (item) => item.productDetail?.deliveryNote?.dnNumber
          )
        )
      )
    })
    const [deliveryNoteList] = useState([])

    useEffect(() => {
      let active = true

      const fetchData = async () => {
        const defaultIds = Array.from(
          new Set(
            (initialValue?.productDescriptions ?? []).map(
              (item) => item.productDetail?.deliveryNoteId
            )
          )
        )
        const response = await fetchInitDeliveryNoteData({
          ids: defaultIds,
        })
        if (!active) return

        setSelectedDeliveryNotes(response)
      }

      fetchData()

      return () => {
        active = false
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      setValue("productDescriptions", initialFormData)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useImperativeHandle(ref, () => ({
      getFormData: () => {
        const data = getValues()
        const dataCopy = (data?.productDescriptions ?? []).map((product) => {
          transformProperty(
            product,
            ["quantity", "dnDetailId", "sequence"],
            parseInt
          )
          transformProperty(product, ["unitPrice"], parseFloat)
          // eslint-disable-next-line no-param-reassign
          if (!product.dnDetailId) delete product.dnDetailId
          // eslint-disable-next-line no-param-reassign
          if (!product.quantity) delete product.quantity
          return product
        })
        return { productDescriptions: dataCopy }
      },
      submitForm: () => {},
    }))

    useEffect(() => {
      const temp = []

      selectedDeliveryNotes.forEach((curr) => {
        const productDetails = simplifyDNProductDetails(
          curr?.productDetails || []
        ).map((item) => {
          return {
            ...item,
            dnNumber: curr?.dnNumber,
          }
        })

        productDetails.forEach((item) => temp.push(item))
      })
      setProductDescriptions(temp)
      setValue("productDescriptions", temp)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeliveryNotes])

    const refRate = useRef(1)

    useEffect(() => {
      if (refRate.current !== 1) {
        setSelectedDeliveryNotes([])
      } else {
        refRate.current += 1
      }
    }, [rateIn])

    const onAddDeliveryNote = (deliveryNote) => {
      if (invoiceDate) {
        if (invoiceDate !== deliveryNote.deliveryDate) {
          showSnackbar("Delivery date not match")
          return
        }
      }
      setInvoiceDate(deliveryNote.deliveryDate)
      const dataCopy = [...selectedDeliveryNotes]
      setSelectedDeliveryNotes([...dataCopy, deliveryNote])
    }

    const onDeleteDeliveryNote = (dnNumber) => {
      const dataCopy = [...selectedDeliveryNotes]
      const newData = dataCopy.filter((item) => item !== dnNumber)

      setSelectedDeliveryNotes(newData)
      if (!newData[0]) {
        setInvoiceDate("")
      }
    }

    useEffect(() => {
      setValue("productDescriptions", initialFormData)
      setProductDescriptions(initialFormData)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ minWidth: "9rem" }}>DN Number</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>UoM</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Sequence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <p style={{ textAlign: "center" }}>No Data</p>
                  </TableCell>
                </TableRow>
              ) : (
                (fields ?? []).map((product, index) => {
                  return (
                    <ProductDescriptionRow
                      key={product.__id}
                      product={product}
                      register={register}
                      errors={errors}
                      index={index}
                    />
                  )
                })
              )}
            </TableBody>
            <TableFooter>
              <DeliveryNotePicker
                selectedDeliveryNotes={selectedDeliveryNotes}
                setSelectedDeliveryNotes={setSelectedDeliveryNotes}
                fetchDeliveryNoteData={fetchDeliveryNoteData}
                onAddItem={onAddDeliveryNote}
                onDeleteItem={onDeleteDeliveryNote}
                deliveryNoteList={deliveryNoteList}
              />
            </TableFooter>
          </Table>
        </TableContainer>
      </>
    )
  }
)

export default ProductDescriptionTable
